import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { omit } from '../../util/common';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { isSessionExpiredError, storableError } from '../../util/errors';
import * as log from '../../util/log';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';
import { applyDerivedListingFields } from '../../util/deriveListingFields';
import { reportClientFailure } from '../../util/clientFailureReport';
import { sanitizeListingLocation } from '../../util/locationPrivacy';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { omitSystemOwnedPublicData } from './EditListingWizard/serverOwnedKeys';

import {
  fetchAvailableTiers as tphApiFetchAvailableTiers,
  previewTierUpgrade as tphApiPreviewTierUpgrade,
  executeTierUpgrade as tphApiExecuteTierUpgrade,
} from '../../util/tphApi';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
        uploadedImages: {},
        uploadedImagesOrder: [],
      }
    : {
        uploadedImages,
        uploadedImagesOrder,
      };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  // Story 15.8 — `scaled-large` is an ASPECT-PRESERVING built-in variant, added
  // here so the framing tool can frame against the real photo. The two
  // variantPrefix derivatives are both 4:3-CROPPED, and framing against an
  // already-cropped image is dishonest. This is the single choke point feeding
  // all six call sites (including uploadImageThunk), so a JUST-UPLOADED photo
  // is framable before the first save. Built-in variants need NO
  // createImageVariantConfig entry.
  //
  // Story 12.13 (AC-7) — `scaled-xlarge` is added BESIDE it. It is what the
  // RENDERER prefers (`adRenderJobProcessor.js` reads scaled-xlarge →
  // scaled-large → default), so the per-photo `maxZoom` ceiling can be computed
  // from the renderer's TRUE source dimensions rather than from whatever the
  // preview happens to display — otherwise a zoom that looks safe here renders
  // soft there. A just-uploaded photo may briefly have neither; the framer's
  // source-side degradation covers that (no aspect-preserving variant ⇒ no
  // cropper; no source dims ⇒ maxZoom 1, zoom control disabled).
  const fieldsImage = [
    `variants.${variantPrefix}`,
    `variants.${variantPrefix}-2x`,
    'variants.scaled-large',
    'variants.scaled-xlarge',
  ];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
export const showListingThunk = createAsyncThunk(
  'EditListingPage/showListing',
  ({ actionPayload, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        // 🔴 Story 12.30 (C2.6, ledger #500) — the one thunk in this file that
        // did not log. The call is browser → Sharetribe, so tph-api never sees
        // it either: without this line a seller locked out of their own listing
        // was visible only when they complained.
        const error = storableError(e);
        log.error(e, 'show-own-listing-failed', {
          listingId: actionPayload?.id?.uuid,
          status: error?.status,
        });
        return rejectWithValue(error);
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestShowListing = (actionPayload, config) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ actionPayload, config })).unwrap();
};

///////////////
// Set Stock //
///////////////
export const setStockThunk = createAsyncThunk(
  'EditListingPage/setStock',
  ({ listingId, oldTotal, newTotal }, { dispatch, rejectWithValue, extra: sdk }) => {
    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
// Set stock if requested among listing update info
export const compareAndSetStock = (listingId, oldTotal, newTotal) => (dispatch, getState, sdk) => {
  return dispatch(setStockThunk({ listingId, oldTotal, newTotal }));
};

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

//////////////////////////
// Create Listing Draft //
//////////////////////////

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export const createListingDraftThunk = createAsyncThunk(
  'EditListingPage/createListingDraft',
  ({ data, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    const { stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    // Story 12.2 — derive-on-save enrichment (incentiveSearch / ageGroup /
    // locationState). Applied in both central save thunks so every wizard
    // step's save — current and future — flows through it.
    // Story 12.28 (AC2) — the location choke point. Every save that reaches
    // Sharetribe passes through here, so the public payload cannot carry a
    // street-level address or an exact coordinate. Sanitize BEFORE deriving:
    // locationState derives from the sanitized address and (AC7) resolves to
    // the same value. privateData is never touched here — the Location panel
    // writes the exact pick (AC3).
    const ownListingValues = applyDerivedListingFields(
      sanitizeListingLocation({ ...imageProperty, ...rest }),
      config
    );

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch).then(() => response);
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestCreateListingDraft = (data, config) => (dispatch, getState, sdk) => {
  return dispatch(createListingDraftThunk({ data, config })).unwrap();
};

////////////////////
// Update Listing //
////////////////////

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
/**
 * Story 12.26 fix pass 2 ([[#1106]]) — what a FAILED listing save may tell Sentry.
 *
 * 🔴 This used to be `{ listingData: data }`: the whole submitted payload, as a
 * Sentry extra, on every failed save of every wizard step. That meant the
 * pedigree step's confirm sent all 21 extracted certificate fields (registered
 * name, registration number, sire, dam…), the location step sent the street
 * address and exact coordinates, and the details step sent the title and
 * description. Reproduced on a real Sentry client's transmitted envelope.
 *
 * An operator debugging a failed save needs WHICH listing, WHICH step, and WHAT
 * KIND of failure — the kind is on the error itself (status / code). They also
 * get the SHAPE of what was submitted: key names only, which are schema names
 * our own listing config defines, never a value a seller typed or a certificate
 * carried. What is deliberately lost is the value that was rejected.
 *
 * Built by explicit reads, never by spreading `data`, so a key added to a
 * payload later cannot ride along by default.
 */
const keysOf = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? Object.keys(value).sort() : [];

export const listingSaveFailureReport = ({ tab, data }) => ({
  listingId: data?.id?.uuid || null,
  tab: tab || null,
  submittedKeys: keysOf(data).filter(key => key !== 'id'),
  publicDataKeys: keysOf(data?.publicData),
  privateDataKeys: keysOf(data?.privateData),
  imageCount: Array.isArray(data?.images) ? data.images.length : null,
});

// 🔴 Ticket 74 (D1) — THE CENTRAL SAVE FILTER, applied to every wizard save of
// an existing listing (`updateListingThunk`) and the papers background persist.
// A panel's publicData is the page-load copy; this drops every key the system
// owns before the SDK sees it, so a save never puts back what tph-api changed
// since the page loaded (sold, on hold, a paid upgrade, last-active, tags, the
// ad image). `listingTier` is dropped only when the stored listing already has
// one. The rule and the list: `EditListingWizard/serverOwnedKeys.js`.
//
// "Stored" is the listing as the store holds it at save time: the same entity
// the panel rendered from, refreshed in this tab by every save's own response.
// It is not a live read of the server, so a tier written elsewhere (tph-api, or
// the seller's own second tab) while this tab's copy has none is not seen —
// ledger #3402. The rule is keyed on "has a tier at all", never on its value,
// so a stale store still protects any listing that had a tier when it loaded.
// Not applied to `createDraft` (a new listing
// has nothing the system wrote) nor to the two single-key writes below (the
// publish stamp and `setDraftListingTierThunk`), which are those keys' writers.
const withoutSystemOwnedPublicData = (values, getState) => {
  if (!values || !values.publicData) return values;
  const storedPublicData = getState().marketplaceData?.entities?.ownListing?.[values.id?.uuid]
    ?.attributes?.publicData;
  return {
    ...values,
    publicData: omitSystemOwnedPublicData(values.publicData, storedPublicData),
  };
};

export const updateListingThunk = createAsyncThunk(
  'EditListingPage/updateListing',
  ({ tab, data, config }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    const { id, stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    // Story 12.2 — derive-on-save enrichment, same as createListingDraftThunk.
    // Story 12.28 (AC2) — same choke point as createListingDraftThunk.
    // Ticket 74 (D1) — the system-owned filter is OUTERMOST, the last step before
    // the SDK (code review LOW-5), so nothing derived or sanitized after it can
    // put a system-owned key back on the wire.
    const ownListingUpdateValues = withoutSystemOwnedPublicData(
      applyDerivedListingFields(sanitizeListingLocation({ id, ...imageProperty, ...rest }), config),
      getState
    );
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        const state = getState();
        const existingTimeZone =
          state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan
            ?.timezone;
        const includedTimeZone = data?.availabilityPlan?.timezone;

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        dispatch(addMarketplaceEntities(response));
        return { response, tab };
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', listingSaveFailureReport({ tab, data }));
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestUpdateListing = (tab, data, config) => (dispatch, getState, sdk) => {
  return dispatch(updateListingThunk({ tab, data, config }))
    .unwrap()
    .then(({ response, tab }) => {
      return response;
    });
};

///////////////////////////////////////////////
// Persist Papers Extraction (story 12.5)    //
///////////////////////////////////////////////

// Background write fired by the wizard-shell polling controller the moment a
// papers-extraction poll returns `complete` (and for terminal-failure
// records). Deliberately a DEDICATED thunk: updateListingThunk's reducer
// cases drive `updateInProgress` (every panel's submit disabled/inProgress
// props) and `updatedTab` (panel "ready" checkmarks) — a background persist
// through it would flicker-disable whatever step the seller is on. This
// thunk has NO reducer cases; it only refreshes the listing entity via
// addMarketplaceEntities so step 4 sees the persisted result without a
// reload. Same queryParams as updateListingThunk so the refreshed entity
// keeps its image relationships mid-photos-step.
export const persistPapersExtractionThunk = createAsyncThunk(
  'EditListingPage/persistPapersExtraction',
  ({ data, config }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    // Derive-on-save wrap (12.2): a persisted birthYear fires the ageGroup
    // derivation; privateData-only payloads pass through untouched.
    // Story 12.28 (AC2) — this thunk carries privateData-only payloads today,
    // which sanitizeListingLocation passes through untouched. It is wrapped all
    // the same.
    //
    // 🔴 CORRECTED (cold review 2026-09-18, L7). This comment used to end "so
    // the property holds for every path into ownListings.update", and that is
    // not true as written: there are FIVE `ownListings.update` / `createDraft`
    // call sites in this file and THREE are wrapped — `createDraft` (draft
    // create), `update` (the wizard save) and this one. The other two,
    // `publishListingPayloadCreator`'s publishedAt stamp and
    // `setDraftListingTierThunk`, carry FIXED
    // LITERAL payloads that cannot hold a location, so there is no exposure —
    // but "no exposure because the payload is a literal" is a different claim
    // from "the choke point covers every path", and only the first one is true.
    // CLAUDE.md: a citation is not evidence; neither is a comment.
    //
    // Ticket 74 (D1) — the system-owned filter wraps TWO of the five: the wizard
    // save and this one. `createDraft` has nothing the system wrote, and the
    // other two ARE the writers of their one key. It is OUTERMOST here too
    // (code review LOW-5).
    const ownListingValues = withoutSystemOwnedPublicData(
      applyDerivedListingFields(sanitizeListingLocation(data), config),
      getState
    );
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.ownListings
      .update(ownListingValues, queryParams)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'persist-papers-extraction-failed', { listingId: data?.id });
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
export const persistPapersExtraction = (data, config) => (dispatch, getState, sdk) => {
  return dispatch(persistPapersExtractionThunk({ data, config })).unwrap();
};

/////////////////////
// Publish Listing //
/////////////////////

const publishListingPayloadCreator = (
  { listingId },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  // Write-once publish stamp. attributes.createdAt is stamped at DRAFT
  // creation, not at publish, so the listing-detail "LISTED … AGO" line needs
  // a true publish time. Set publicData.publishedAt the first time a listing
  // is published; never overwrite it on a later unpublish/republish.
  const existingListing = getState().marketplaceData.entities.ownListing?.[listingId.uuid];
  const alreadyStamped = !!existingListing?.attributes?.publicData?.publishedAt;

  // Partial publicData update: Sharetribe shallow-merges the supplied keys into
  // the listing's existing publicData, so sibling keys are preserved (not
  // clobbered). Skipped entirely when the stamp is already present.
  const stampMaybe = alreadyStamped
    ? Promise.resolve()
    : sdk.ownListings.update({
        id: listingId,
        publicData: { publishedAt: new Date().toISOString() },
      });

  return stampMaybe
    .then(() => sdk.ownListings.publishDraft({ id: listingId }, { expand: true }))
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const publishListingThunk = createAsyncThunk(
  'EditListingPage/publishListing',
  publishListingPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestPublishListingDraft = listingId => (dispatch, getState, sdk) => {
  return dispatch(publishListingThunk({ listingId })).unwrap();
};

//////////////////
// Upload Image //
//////////////////

// Images return imageId which we need to map with previously generated temporary id
export const uploadImageThunk = createAsyncThunk(
  'EditListingPage/uploadImage',
  ({ actionPayload, listingImageConfig }, { rejectWithValue, extra: sdk }) => {
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return {
          data: { ...img, id: actionPayload.id, imageId: img.id, file: actionPayload.file },
        };
      })
      .catch(e => {
        return rejectWithValue({ id: actionPayload.id, error: storableError(e) });
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestImageUpload = (actionPayload, listingImageConfig) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(uploadImageThunk({ actionPayload, listingImageConfig })).unwrap();
};

///////////////////////////////
// Add AvailabilityException //
///////////////////////////////
const addAvailabilityExceptionPayloadCreator = ({ params }, { rejectWithValue, extra: sdk }) => {
  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return { data: availabilityException };
    })
    .catch(e => {
      return rejectWithValue({ error: storableError(e) });
    });
};

export const addAvailabilityExceptionThunk = createAsyncThunk(
  'EditListingPage/addAvailabilityException',
  addAvailabilityExceptionPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  return dispatch(addAvailabilityExceptionThunk({ params })).unwrap();
};

//////////////////////////////////
// Delete AvailabilityException //
//////////////////////////////////
const deleteAvailabilityExceptionPayloadCreator = ({ params }, { rejectWithValue, extra: sdk }) => {
  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return { data: availabilityException };
    })
    .catch(e => {
      return rejectWithValue({ error: storableError(e) });
    });
};

export const deleteAvailabilityExceptionThunk = createAsyncThunk(
  'EditListingPage/deleteAvailabilityException',
  deleteAvailabilityExceptionPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  return dispatch(deleteAvailabilityExceptionThunk({ params })).unwrap();
};

//////////////////////////////////
// Fetch AvailabilityExceptions //
//////////////////////////////////
const fetchAvailabilityExceptionsPayloadCreator = (
  { params },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsThunk.fulfilled({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return {
        ...timeUnitIdProp,
        exceptions: availabilityExceptions,
      };
    })
    .catch(e => {
      return rejectWithValue({ ...timeUnitIdProp, error: storableError(e) });
    });
};

export const fetchAvailabilityExceptionsThunk = createAsyncThunk(
  'EditListingPage/fetchAvailabilityExceptions',
  fetchAvailabilityExceptionsPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  return dispatch(fetchAvailabilityExceptionsThunk({ params })).unwrap();
};

export const fetchExtraAvailabilityExceptionsThunk = createAsyncThunk(
  'EditListingPage/fetchExtraAvailabilityExceptions',
  ({ data }) => data
);

//////////////////////////////////////////////
// Story 4.5.2 — Fetch tph-api availableTiers
//////////////////////////////////////////////
// Cross-origin GET /api/v1/seller/available-tiers via src/util/tphApi.js.
// Drives the EditListingDetailsPanel's auto-assign / multi-tier picker /
// zero-available CTA branches (AC4). The rejectWithValue payload is the
// error.message string (NOT_AUTHENTICATED / TOKEN_REJECTED /
// TPH_API_UNAVAILABLE) so the panel can branch on a stable vocabulary.
// Story 12.10 AC1 — session-expiry refresh-and-retry. tphApi.js reads the
// access token raw from the cookie with no refresh; long idle (or the
// mount-time race) leaves a stale token → NOT_AUTHENTICATED / TOKEN_REJECTED
// on a fully recoverable session. A cheap authenticated SDK call triggers the
// SDK's 401→refresh→SaveToken chain, which rewrites the cookie; the retry
// then re-reads the fresh token. Exactly ONE retry, three-way classification:
//   - refresh + retry succeed → fulfilled (seller never sees an error)
//   - refresh rejects 401-classified → NOT_AUTHENTICATED (session truly dead;
//     the SDK's anon fallback has overwritten the cookie — AC3 recovery
//     surface). 12.10-review refinement: a NON-401 refresh rejection (network
//     blip, 5xx) is NOT session death — it rejects TOKEN_REJECTED so the
//     seller lands on the generic retryable copy; a transient blip must never
//     tell a seller they've been logged out.
//   - refresh succeeds but the retry fails → TOKEN_REJECTED (the documented
//     three-way is exact: every retry failure classifies as a tph-api-side
//     rejection, never as session expiry)
//
// Story 10.19 (D1, D4) — every TERMINAL rejection of the three tier thunks is
// reported to our own server (surface `edit_listing`), and returns exactly the
// rejection it returned before: the payload, its shape and its timing are
// unchanged (AC1.5). A failure the refresh-and-retry heals is not a failure
// and sends nothing. `failure` is what the attempt finally failed WITH — the
// retry's own error, not the code the thunk maps it to — so a retry that hit a
// CORS block reports `tph_api_unreachable` rather than `token_rejected`.
// `already_at_tier` is the modal's success / information path: not reported.
const reportAndReject = (rejectWithValue, action, failure, payload) => {
  if (!(payload && payload.code === 'already_at_tier')) {
    reportClientFailure({ surface: 'edit_listing', action, error: failure });
  }
  return rejectWithValue(payload);
};

export const fetchAvailableTiersThunk = createAsyncThunk(
  'EditListingPage/fetchAvailableTiers',
  async (_arg, { rejectWithValue, extra: sdk }) => {
    try {
      const result = await tphApiFetchAvailableTiers();
      return result;
    } catch (e) {
      const code = (e && e.message) || 'TPH_API_UNAVAILABLE';
      if (code !== 'NOT_AUTHENTICATED' && code !== 'TOKEN_REJECTED') {
        return reportAndReject(rejectWithValue, 'tier_fetch', e, code);
      }
      try {
        await sdk.currentUser.show();
      } catch (refreshError) {
        const refreshCode = isSessionExpiredError(refreshError)
          ? 'NOT_AUTHENTICATED'
          : 'TOKEN_REJECTED';
        return reportAndReject(rejectWithValue, 'tier_fetch', refreshCode, refreshCode);
      }
      try {
        const result = await tphApiFetchAvailableTiers();
        return result;
      } catch (retryError) {
        return reportAndReject(rejectWithValue, 'tier_fetch', retryError, 'TOKEN_REJECTED');
      }
    }
  }
);
// Backward compatible wrapper following the existing duck convention.
export const requestFetchAvailableTiers = () => (dispatch, getState, sdk) => {
  return dispatch(fetchAvailableTiersThunk()).unwrap();
};

//////////////////////////////////////////////
// Story 13.4 — tier-upgrade preview + execute
//////////////////////////////////////////////
// Mirrors fetchAvailableTiersThunk's shape: a thin createAsyncThunk that relays
// to src/util/tphApi.js and surfaces the WHOLE typed envelope
// `{ code, status, reason }` as rejectWithValue so the modal can branch on a
// stable vocabulary. The `code` vocabulary is NOT_AUTHENTICATED /
// TOKEN_REJECTED / TPH_API_UNAVAILABLE / TIER_UPGRADE_FAILED / a typed server
// code.
//
// ⚠️ Story 13.4 T2 (ID-1) — THIS IS THE SEAM, and it must stay an OBJECT.
// rejectWithValue previously received the code STRING. RTK's unwrap() throws the
// payload verbatim, and a string primitive has no `.code` or `.message`, so the
// modal's `e.code || e.message` evaluated to undefined and EVERY failure — all 18
// branches — collapsed into the generic message. The per-code map in
// TierUpgradeModal.js was unreachable from the commit that created it. If you
// are tempted to "simplify" this back to a string, that is the defect returning.
//
// Plain object, never the Error instance: store.js disables serializableCheck so
// an Error would not warn, but the reducer stores this payload and a plain object
// is the RTK-idiomatic shape. The duck test asserts it with toStrictEqual (ID-13),
// which fails on an Error prototype.
//
// The tier-upgrade routes are LIVE on staging (tph-api 920ec4f), so the 12.10
// session-refresh dance is now extended here: a stale access token → 401 →
// "Pricing unavailable" would otherwise dead-end with no self-heal. Both thunks
// detect the NOT_AUTHENTICATED / TOKEN_REJECTED vocabulary, force one SDK token
// refresh via sdk.currentUser.show(), and retry the tph-api call exactly ONCE.
// Typed 4xx codes (402/403/409/422) never match the auth vocabulary, so they
// pass straight through to the modal's typed-error branches untouched — and a
// retry that fails with a typed 4xx keeps its own code (unlike the tiers read
// path, whose endpoint has no typed-4xx surface to preserve).
//
// Normalise whatever tphApi threw into the payload the modal branches on.
// `fallbackCode` preserves each call site's own default: the retry-failure
// branches classify an opaque failure as TOKEN_REJECTED, everything else as
// TPH_API_UNAVAILABLE.
export const toTierUpgradeRejection = (e, fallbackCode = 'TPH_API_UNAVAILABLE') => ({
  code: (e && (e.code || e.message)) || fallbackCode,
  status: e && e.status != null ? e.status : null,
  reason: e && e.reason != null ? e.reason : null,
});

// PREVIEW is a pure read: it only quotes a proration price and charges nothing,
// so a single free retry after a refresh is unconditionally safe.
export const previewTierUpgradeThunk = createAsyncThunk(
  'EditListingPage/previewTierUpgrade',
  async (args, { rejectWithValue, extra: sdk }) => {
    try {
      return await tphApiPreviewTierUpgrade(args);
    } catch (e) {
      // The refresh-and-retry decision still reads the STRING code; only the
      // rejected payload changes shape (T2).
      const code = (e && (e.code || e.message)) || 'TPH_API_UNAVAILABLE';
      if (code !== 'NOT_AUTHENTICATED' && code !== 'TOKEN_REJECTED') {
        return reportAndReject(
          rejectWithValue,
          'tier_upgrade_preview',
          e,
          toTierUpgradeRejection(e)
        );
      }
      try {
        await sdk.currentUser.show();
      } catch (refreshError) {
        const refreshCode = isSessionExpiredError(refreshError)
          ? 'NOT_AUTHENTICATED'
          : 'TOKEN_REJECTED';
        return reportAndReject(rejectWithValue, 'tier_upgrade_preview', refreshCode, {
          code: refreshCode,
          status: null,
          reason: null,
        });
      }
      try {
        return await tphApiPreviewTierUpgrade(args);
      } catch (retryError) {
        return reportAndReject(
          rejectWithValue,
          'tier_upgrade_preview',
          retryError,
          toTierUpgradeRejection(retryError, 'TOKEN_REJECTED')
        );
      }
    }
  }
);
export const requestPreviewTierUpgrade = args => dispatch =>
  dispatch(previewTierUpgradeThunk(args)).unwrap();

// EXECUTE is the charge path — auto-retry is safe here ONLY because the 401 is
// strictly PRE-CHARGE. In tierUpgrade.js the requireCustomerToken middleware
// (routes/tierUpgrade.js:356) runs BEFORE the async handler that calls
// tierUpgrade.performUpgrade (the Stripe collect, :385); a rejected token
// short-circuits at the middleware (customerAuth.js 401 → res.status(401),
// never next()), so the handler — and every Stripe operation — never runs.
// The handler's own post-charge failures surface as 402/500 with typed codes
// (collect_failed / charged_but_tier_not_persisted), NEVER as
// NOT_AUTHENTICATED / TOKEN_REJECTED. So the retry branch below can only fire
// on a pre-charge 401 and can never double-charge. Single retry, no loop.
export const executeTierUpgradeThunk = createAsyncThunk(
  'EditListingPage/executeTierUpgrade',
  async (args, { rejectWithValue, extra: sdk }) => {
    try {
      return await tphApiExecuteTierUpgrade(args);
    } catch (e) {
      // The refresh-and-retry decision still reads the STRING code; only the
      // rejected payload changes shape (T2).
      const code = (e && (e.code || e.message)) || 'TPH_API_UNAVAILABLE';
      if (code !== 'NOT_AUTHENTICATED' && code !== 'TOKEN_REJECTED') {
        return reportAndReject(rejectWithValue, 'tier_upgrade', e, toTierUpgradeRejection(e));
      }
      try {
        await sdk.currentUser.show();
      } catch (refreshError) {
        const refreshCode = isSessionExpiredError(refreshError)
          ? 'NOT_AUTHENTICATED'
          : 'TOKEN_REJECTED';
        return reportAndReject(rejectWithValue, 'tier_upgrade', refreshCode, {
          code: refreshCode,
          status: null,
          reason: null,
        });
      }
      try {
        return await tphApiExecuteTierUpgrade(args);
      } catch (retryError) {
        return reportAndReject(
          rejectWithValue,
          'tier_upgrade',
          retryError,
          toTierUpgradeRejection(retryError, 'TOKEN_REJECTED')
        );
      }
    }
  }
);
export const requestExecuteTierUpgrade = args => dispatch =>
  dispatch(executeTierUpgradeThunk(args)).unwrap();

// Story 13.4 (iteration 2) — DRAFT-path listingTier write. The frontend owns
// the tier write ONLY for drafts (a published listing's tier is written
// server-side by 13.3, lockstep — the published path re-fetches instead). This
// mirrors persistPapersExtractionThunk: a DEDICATED, reducer-case-less thunk
// that only refreshes the listing entity via addMarketplaceEntities so the
// locked feature gate re-reads publicData.listingTier and unlocks in place — no
// updateListingThunk reducer churn that would flicker-disable the current step.
// Same image-variant queryParams so the refreshed entity keeps its image
// relationships mid-photos-step (where the video upgrade is triggered).
export const setDraftListingTierThunk = createAsyncThunk(
  'EditListingPage/setDraftListingTier',
  ({ listingId, tier, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.ownListings
      .update({ id: new UUID(listingId), publicData: { listingTier: tier } }, queryParams)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'set-draft-listing-tier-failed', { listingId, tier });
        return rejectWithValue(storableError(e));
      });
  }
);
export const requestSetDraftListingTier = (listingId, tier, config) => (dispatch, getState, sdk) =>
  dispatch(setDraftListingTierThunk({ listingId, tier, config })).unwrap();

// Story 13.4 (iteration 2) — PUBLISHED-path refresh. After a paid upgrade on a
// published listing, 13.3 has written listingTier server-side; the frontend
// just re-fetches the entity (reuses showListingThunk → addMarketplaceEntities)
// so the gate re-reads the new tier. Thin wrapper that builds the UUID so the
// modal can pass a plain string id.
export const requestRefetchListing = (listingId, config) => (dispatch, getState, sdk) =>
  dispatch(showListingThunk({ actionPayload: { id: new UUID(listingId) }, config })).unwrap();

/////////////////////////
// Save Payout Details //
/////////////////////////
const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      return response;
    })
    .catch(() => {
      return rejectWithValue();
    });
};

export const savePayoutDetailsThunk = createAsyncThunk(
  'EditListingPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrappers for the thunks
export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

//////////////////////////////////////////////////
// Story 12.12 — Draft resume on /l/new entry   //
//////////////////////////////////////////////////

// Query the current user's own listings and keep only drafts, newest first.
// NOTE (load-bearing API correction): own_listings/query does NOT support a
// `states` filter param — passing one is silently ignored — so the draft
// filter MUST stay client-side. `include: ['images']` rides along (same
// ownListings.query precedent as ManageListingsPage) so the resume card's
// "step you stopped on" derivation (tabCompleted) can see whether the Photos
// step is actually complete; title/state/createdAt/publicData already ride on
// attributes by default.
export const queryOwnDraftsThunk = createAsyncThunk(
  'EditListingPage/queryOwnDrafts',
  (_, { extra: sdk, rejectWithValue }) => {
    return sdk.ownListings
      .query({ perPage: 100, include: ['images'] })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        return entities
          .filter(l => l?.attributes?.state === LISTING_STATE_DRAFT)
          .sort(
            (a, b) => new Date(b.attributes.createdAt || 0) - new Date(a.attributes.createdAt || 0)
          );
      })
      .catch(e => {
        // Non-fatal by design: the card just doesn't render. The blank wizard
        // must never be blocked or errored by this query.
        log.error(e, 'query-own-drafts-failed');
        return rejectWithValue(storableError(e));
      });
  }
);

// Persist (or clear, with null) the zero-slot parked draft id on the current
// user's privateData. Single-key shallow merge only — sibling privateData keys
// (favorites, phone, onboardingCompletedAt, …) are preserved by the top-level
// merge, per the favorites.duck precedent. Never write the whole object.
export const savePendingDraftIdThunk = createAsyncThunk(
  'EditListingPage/savePendingDraftId',
  (pendingDraftId, { extra: sdk, rejectWithValue }) => {
    return sdk.currentUser.updateProfile({ privateData: { pendingDraftId } }).catch(e => {
      log.error(e, 'save-pending-draft-id-failed', { pendingDraftId });
      return rejectWithValue(storableError(e));
    });
  }
);
// Backward compatible wrappers for the thunk
export const savePendingDraftId = pendingDraftId => dispatch =>
  dispatch(savePendingDraftIdThunk(pendingDraftId)).unwrap();
export const clearPendingDraftId = () => dispatch =>
  dispatch(savePendingDraftIdThunk(null)).unwrap();

////////////////////////////////
// Fetch Load Data Exceptions //
////////////////////////////////

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// ================ Slice ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  // Story 4.5.2 — tph-api available-tiers fetch. `availableTiers` is null
  // until the fetch completes; on success it becomes the array (including
  // [] for the zero-available case) so the panel can distinguish "not yet
  // fetched" from "fetched, but the seller has zero slots".
  availableTiers: null,
  tiersFetchInProgress: false,
  tiersFetchError: null,
  // Story 4.5.2 — retain the tph-api request_id for observability so a seller
  // who hits an error can quote it to support / the dev team can correlate
  // the marketplace failure with the tph-api log line.
  tiersFetchRequestId: null,
  // Story 13.4 — tier-upgrade modal. The PREVIEW drives the modal's "$X today"
  // (amountDue) + "$X/mo" (targetMonthly) lines; both stay null until the
  // proration preview returns (HELD-LOCAL endpoint → may simply error, which
  // the modal renders as a graceful "unavailable" state). EXECUTE is the
  // commit (iteration 2 wires its success path); the state lives here now so
  // the modal can already reflect in-flight / error without a follow-up duck change.
  tierUpgradePreview: null,
  tierUpgradePreviewInProgress: false,
  tierUpgradePreviewError: null,
  tierUpgradeExecuteInProgress: false,
  tierUpgradeExecuteError: null,
  // Story 12.12 — own drafts fetched on /l/new entry (resume-prompt card +
  // pendingDraftId return leg). `ownDraftsQueried` distinguishes "not yet
  // resolved" from "resolved with zero drafts" so the return-leg staleness
  // decision never fires against an unfinished query.
  ownDrafts: [],
  ownDraftsQueried: false,
  queryOwnDraftsError: null,
};

const editListingPageSlice = createSlice({
  name: 'EditListingPage',
  initialState,
  reducers: {
    markTabUpdated: (state, action) => {
      state.updatedTab = action.payload;
    },
    clearUpdatedTab: state => {
      state.updatedTab = null;
      state.updateListingError = null;
    },
    clearPublishError: state => {
      state.publishListingError = null;
    },
    removeListingImage: (state, action) => {
      const id = action.payload;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      state.uploadedImages = uploadedImages;
      state.uploadedImagesOrder = uploadedImagesOrder;
      state.removedImageIds = removedImageIds;
    },
  },
  extraReducers: builder => {
    builder
      // createListingDraft cases
      .addCase(createListingDraftThunk.pending, state => {
        state.createListingDraftInProgress = true;
        state.createListingDraftError = null;
        state.submittedListingId = null;
        state.listingDraft = null;
      })
      .addCase(createListingDraftThunk.fulfilled, (state, action) => {
        const updatedImagesState = updateUploadedImagesState(state, action.payload.data);
        state.uploadedImages = updatedImagesState.uploadedImages;
        state.uploadedImagesOrder = updatedImagesState.uploadedImagesOrder;
        state.createListingDraftInProgress = false;
        state.submittedListingId = action.payload.data.data.id;
        state.listingDraft = action.payload.data.data;
      })
      .addCase(createListingDraftThunk.rejected, (state, action) => {
        state.createListingDraftInProgress = false;
        state.createListingDraftError = action.payload;
      })
      // publishListing cases
      .addCase(publishListingThunk.pending, (state, action) => {
        state.listingId = action.meta.arg.listingId;
        state.publishListingError = null;
      })
      .addCase(publishListingThunk.fulfilled, state => {
        state.redirectToListing = true;
        state.createListingDraftError = null;
        state.updateListingError = null;
        state.showListingsError = null;
        state.uploadImageError = null;
        state.createListingDraftInProgress = false;
        state.updateInProgress = false;
      })
      .addCase(publishListingThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.publishListingError = {
          listingId: state.listingId,
          error: action.payload,
        };
      })
      // updateListing cases
      .addCase(updateListingThunk.pending, state => {
        state.updateInProgress = true;
        state.updateListingError = null;
      })
      .addCase(updateListingThunk.fulfilled, (state, action) => {
        const updatedImagesState = updateUploadedImagesState(state, action.payload.response.data);
        state.uploadedImages = updatedImagesState.uploadedImages;
        state.uploadedImagesOrder = updatedImagesState.uploadedImagesOrder;
        state.updateInProgress = false;
        state.updatedTab = action.payload.tab;
      })
      .addCase(updateListingThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateListingError = action.payload;
      })
      // showListing cases
      .addCase(showListingThunk.pending, state => {
        state.showListingsError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        const listingIdFromPayload = action.payload.data.data.id;
        const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
        // If listing stays the same, we trust previously fetched exception data.
        if (listingIdFromPayload?.uuid === state.listingId?.uuid) {
          Object.assign(state, initialState);
          state.listingId = listingId;
          state.allExceptions = allExceptions;
          state.weeklyExceptionQueries = weeklyExceptionQueries;
          state.monthlyExceptionQueries = monthlyExceptionQueries;
        } else {
          Object.assign(state, initialState);
          state.listingId = listingIdFromPayload;
        }
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.showListingsError = action.payload;
        state.redirectToListing = false;
      })
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const params = action.meta.arg.actionPayload;
        const id = params.id;
        // payload.params: { id: 'tempId', file }
        const uploadedImages = {
          ...state.uploadedImages,
          [id]: { ...params },
        };
        state.uploadedImages = uploadedImages;
        state.uploadedImagesOrder = state.uploadedImagesOrder.concat([id]);
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        // payload.data: { id: 'tempId', imageId: 'some-real-id', attributes, type }
        const { id, ...rest } = action.payload.data;
        state.uploadedImages[id] = { id, ...rest };
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        const { id, error } = action.payload;
        state.uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
        state.uploadedImages = omit(state.uploadedImages, id);
        state.uploadImageError = error;
      })
      // setStock cases
      .addCase(setStockThunk.pending, state => {
        state.setStockInProgress = true;
        state.setStockError = null;
      })
      .addCase(setStockThunk.fulfilled, state => {
        state.setStockInProgress = false;
      })
      .addCase(setStockThunk.rejected, (state, action) => {
        state.setStockInProgress = false;
        state.setStockError = action.payload;
      })
      // fetchAvailabilityExceptions cases
      .addCase(fetchAvailabilityExceptionsThunk.pending, (state, action) => {
        const { isWeekly, start, timeZone } = action.meta.arg.params;
        const weekStartId = isWeekly ? stringifyDateToISO8601(start) : null;
        const monthId = !isWeekly ? monthIdString(start, timeZone) : null;
        const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      .addCase(fetchAvailabilityExceptionsThunk.fulfilled, (state, action) => {
        const { exceptions, monthId, weekStartId } = action.payload;
        const combinedExceptions = state.allExceptions.concat(exceptions);
        const selectId = x => x.id.uuid;
        state.allExceptions = uniqueBy(combinedExceptions, selectId).sort(
          sortExceptionsByStartTime
        );

        const newData = { fetchExceptionsInProgress: false };
        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      .addCase(fetchAvailabilityExceptionsThunk.rejected, (state, action) => {
        const { monthId, weekStartId, error } = action.payload;
        const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      // fetchExtraAvailabilityExceptions cases
      .addCase(fetchExtraAvailabilityExceptionsThunk.fulfilled, (state, action) => {
        const combinedExceptions = state.allExceptions.concat(action.payload.exceptions);
        const selectId = x => x.id.uuid;
        state.allExceptions = uniqueBy(combinedExceptions, selectId).sort(
          sortExceptionsByStartTime
        );
      })
      // addAvailabilityException cases
      .addCase(addAvailabilityExceptionThunk.pending, state => {
        state.addExceptionError = null;
        state.addExceptionInProgress = true;
      })
      .addCase(addAvailabilityExceptionThunk.fulfilled, (state, action) => {
        const exception = action.payload.data;
        const combinedExceptions = state.allExceptions.concat(exception);
        state.allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
        state.addExceptionInProgress = false;
      })
      .addCase(addAvailabilityExceptionThunk.rejected, (state, action) => {
        state.addExceptionError = action.payload.error;
        state.addExceptionInProgress = false;
      })
      // deleteAvailabilityException cases
      .addCase(deleteAvailabilityExceptionThunk.pending, state => {
        state.deleteExceptionError = null;
        state.deleteExceptionInProgress = true;
      })
      .addCase(deleteAvailabilityExceptionThunk.fulfilled, (state, action) => {
        const exception = action.payload.data;
        const id = exception.id.uuid;
        state.allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
        state.deleteExceptionInProgress = false;
      })
      .addCase(deleteAvailabilityExceptionThunk.rejected, (state, action) => {
        state.deleteExceptionError = action.payload.error;
        state.deleteExceptionInProgress = false;
      })
      // savePayoutDetails cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      })
      // Story 4.5.2 — fetchAvailableTiers cases
      .addCase(fetchAvailableTiersThunk.pending, state => {
        state.tiersFetchInProgress = true;
        state.tiersFetchError = null;
      })
      .addCase(fetchAvailableTiersThunk.fulfilled, (state, action) => {
        state.tiersFetchInProgress = false;
        state.tiersFetchError = null;
        state.availableTiers = action.payload?.availableTiers || [];
        state.tiersFetchRequestId = action.payload?.request_id || null;
      })
      .addCase(fetchAvailableTiersThunk.rejected, (state, action) => {
        state.tiersFetchInProgress = false;
        state.tiersFetchError = action.payload || 'TPH_API_UNAVAILABLE';
      })
      // Story 13.4 — tier-upgrade preview cases (drives "$X today" + "$X/mo").
      .addCase(previewTierUpgradeThunk.pending, state => {
        state.tierUpgradePreviewInProgress = true;
        state.tierUpgradePreviewError = null;
        state.tierUpgradePreview = null;
      })
      .addCase(previewTierUpgradeThunk.fulfilled, (state, action) => {
        state.tierUpgradePreviewInProgress = false;
        state.tierUpgradePreviewError = null;
        state.tierUpgradePreview = action.payload || null;
      })
      .addCase(previewTierUpgradeThunk.rejected, (state, action) => {
        state.tierUpgradePreviewInProgress = false;
        // ID-1(a): this field holds the payload OBJECT, so state carries exactly
        // what the seam carries and there is one vocabulary, not two.
        state.tierUpgradePreviewError = action.payload || {
          code: 'TPH_API_UNAVAILABLE',
          status: null,
          reason: null,
        };
        state.tierUpgradePreview = null;
      })
      // Story 13.4 — tier-upgrade execute cases (success path wired in iteration 2).
      .addCase(executeTierUpgradeThunk.pending, state => {
        state.tierUpgradeExecuteInProgress = true;
        state.tierUpgradeExecuteError = null;
      })
      .addCase(executeTierUpgradeThunk.fulfilled, state => {
        state.tierUpgradeExecuteInProgress = false;
        state.tierUpgradeExecuteError = null;
      })
      .addCase(executeTierUpgradeThunk.rejected, (state, action) => {
        state.tierUpgradeExecuteInProgress = false;
        // ID-1(a): payload OBJECT, same vocabulary as the seam.
        state.tierUpgradeExecuteError = action.payload || {
          code: 'TPH_API_UNAVAILABLE',
          status: null,
          reason: null,
        };
      })
      // Story 12.12 — own-drafts query cases (resume card on /l/new)
      .addCase(queryOwnDraftsThunk.pending, state => {
        state.queryOwnDraftsError = null;
      })
      .addCase(queryOwnDraftsThunk.fulfilled, (state, action) => {
        state.ownDrafts = action.payload;
        state.ownDraftsQueried = true;
      })
      .addCase(queryOwnDraftsThunk.rejected, (state, action) => {
        state.ownDrafts = [];
        state.ownDraftsQueried = true;
        state.queryOwnDraftsError = action.payload;
      });
  },
});

export const {
  markTabUpdated,
  clearUpdatedTab,
  clearPublishError,
  removeListingImage,
} = editListingPageSlice.actions;
export default editListingPageSlice.reducer;

// ================ Load data ================ //

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  const { id, type } = params;
  const fetchCurrentUserOptions = {
    updateNotifications: false,
  };

  if (type === 'new') {
    // No need to listing data when creating a new listing.
    // Story 12.12 — additionally query own drafts for the resume-prompt card
    // and the pendingDraftId return leg. Dispatching an asyncThunk never
    // rejects (failures land in queryOwnDraftsError), so a drafts-query
    // failure can neither block nor error the blank wizard.
    return Promise.all([
      dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
      dispatch(queryOwnDraftsThunk()),
    ])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([
    dispatch(requestShowListing(payload, config)),
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
  ])
    .then(response => {
      const currentUser = getState().user.currentUser;

      // Do not fetch extra information if user is in pending-approval state.
      if (isUserAuthorized(currentUser)) {
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        // Because of two dispatch functions, response is an array.
        // We are only interested in the response from requestShowListing here,
        // so we need to pick the first one
        const listing = response[0]?.data?.data;
        const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
        if (listing && isBookingProcessAlias(transactionProcessAlias)) {
          fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
        }
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};
